import CarbonEmissionPanel from "@components/Review/CarbonEmissionPanel";
import { Typography } from "@hellocarbo/lasagne";
import { use } from "react";
import { useTranslation } from "react-i18next";
import {
  data,
  LoaderFunctionArgs,
  useLoaderData,
  useParams,
} from "react-router";

import { PublicReviewBanner, ReviewBanner } from "@/components/Banner";
import { NavigationMenuLinks } from "@/components/NavigationMenu";
import { UserContext } from "@/contexts";
import { CustomUIMatch } from "@/hooks/useBreadcrumbs";
import { Policy } from "@/types";
import { formatEndDate } from "@/utils/formatDate";

import { useReviewContext } from "./Review";

export const handle = {
  activeMenuLink: () => NavigationMenuLinks.restitution,
  breadcrumb: ({ reviewId: paramsReviewId, admin }) => {
    const reviewId = paramsReviewId ?? localStorage.getItem("currentReviewId");
    return [
      {
        href: `/reviews/${reviewId}${admin ? "/admin" : ""}`,
        label: `navigation-menu:pages.analyse`,
      },
    ];
  },
} as CustomUIMatch["handle"];

export async function loader({ params }: LoaderFunctionArgs) {
  const { slug, reviewId, admin } = params;

  const isPublic = !reviewId && slug;
  const isAdmin = Boolean(admin);

  let comparisonPolicyEndpoint = "/api/client/policies/comparison"; // Review
  if (isPublic) {
    comparisonPolicyEndpoint = `/api/client/policies/comparison?review_slug=${slug}`; // Public review (as a logged in or offline user)
  } else if (isAdmin) {
    comparisonPolicyEndpoint = `/api/client/policies/comparison?review_id=${reviewId}`; // Review (as an admin, via the manager)
  }

  // Fetch the comparison policy
  const policyResponse = await fetch(comparisonPolicyEndpoint);

  if (policyResponse.status !== 200) {
    throw data("Failed to load the comparison policy", {
      status: policyResponse.status,
    });
  }

  const { data: comparisonPolicy } = (await policyResponse.json()) as {
    data: Policy;
  };

  return { comparisonPolicy };
}

function ReviewIndex() {
  const { reviewData, industryData } = useReviewContext();
  const { slug, reviewId } = useParams();
  const { comparisonPolicy } = useLoaderData<typeof loader>();

  const isPublicReview = !reviewId && slug;

  const { t } = useTranslation(["common", "review"]);

  if (!reviewData || !industryData) {
    return (
      <div className="grid place-items-center">
        <p>{t("common:loading")}</p>
      </div>
    );
  }

  const {
    company_name: companyName,
    period_start: periodStart,
    period_end: periodEnd,
  } = reviewData;

  const { user } = use(UserContext);

  return (
    <>
      <header className="mb-md">
        <Typography step="3" as="h1" weight="bold" className="mb-3xs">
          {t("review:title", { companyName })}
        </Typography>
        <Typography className="text-grayscale-dark">
          {t("formatter:date", { value: new Date(periodStart) })}
          &nbsp;-&nbsp;
          {t("formatter:date", {
            value: formatEndDate(new Date(periodEnd)),
          })}
        </Typography>
      </header>
      <CarbonEmissionPanel
        data={reviewData}
        comparisonPolicy={comparisonPolicy}
      />
      {!isPublicReview && user && (
        <ReviewBanner linkUrl={`/reviews/${reviewId}/feedback`} />
      )}
      {isPublicReview && !user && (
        <PublicReviewBanner companyName={companyName} />
      )}
    </>
  );
}

export default ReviewIndex;
